import type {
  DashboardTrend,
  VolumeStressBand,
} from '@/services/api/dashboard';

/**
 * Data-quality rule (dock-context-v1): null always means "no data" and must
 * render as "—", never 0 — every formatter here follows it.
 */

/** Seconds-per-item headline, e.g. "8.1s" — the canonical comparison unit. */
export const fmtSecPerItem = (v: number | null | undefined): string =>
  v == null ? '—' : `${v >= 100 ? Math.round(v) : v.toFixed(1)}s`;

/** Duration in human units, e.g. "8m 15s", "1h 04m", "45s". */
export const fmtDuration = (sec: number | null | undefined): string => {
  if (sec == null) return '—';
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
};

export const fmtInt = (v: number | null | undefined): string =>
  v == null ? '—' : v.toLocaleString('en-US');

export const fmtPct = (v: number | null | undefined): string =>
  v == null ? '—' : `${Math.round(v * 100)}%`;

/** Signed percent-change chip text, e.g. "+8.2%". */
export const fmtTrendDelta = (trend: DashboardTrend): string => {
  if (trend.percentChange == null) return '—';
  const sign = trend.percentChange > 0 ? '+' : '';
  return `${sign}${trend.percentChange.toFixed(1)}%`;
};

/**
 * For velocities and stress, DOWN is good (faster / less strain). Map the
 * raw direction to the delta chip's color semantics.
 */
export const trendTone = (
  direction: DashboardTrend['direction'],
  lowerIsBetter = true
): 'up' | 'down' | 'flat' => {
  if (direction === 'flat') return 'flat';
  const improved = lowerIsBetter ? direction === 'down' : direction === 'up';
  return improved ? 'up' : 'down';
};

export const VOLUME_STRESS_LABELS: Record<VolumeStressBand, string> = {
  NORMAL: 'Normal',
  ELEVATED: 'Elevated',
  AT_CEILING: 'At ceiling',
};

export const VOLUME_STRESS_STATUS: Record<
  VolumeStressBand,
  'success' | 'warning' | 'error'
> = {
  NORMAL: 'success',
  ELEVATED: 'warning',
  AT_CEILING: 'error',
};

/** WIB date label for chart axes: "07-10" from "2026-07-10". */
export const shortDate = (isoDate: string): string => isoDate.slice(5);
